import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, FileCheck2, Phone, Recycle, Handshake, KeyRound } from "lucide-react";
import { SITE, ABOUT_IMAGE, INTERIOR_IMAGE } from "@/lib/site";
import { Reveal, SectionHeading } from "@/components/Reveal";

const STEPS = [
  {
    icon: Phone,
    title: "1. Kontakt",
    text: "Ring oss eller skicka ett meddelande. Berätta vad du vill — köpa, sälja eller byta.",
  },
  {
    icon: Handshake,
    title: "2. Visning & värdering",
    text: "Vi ses på Viaredsvägen, tittar på fordon tillsammans och går igenom villkoren.",
  },
  {
    icon: KeyRound,
    title: "3. Klart — kör hem",
    text: "Papper och överlämning fixar vi. Du kan rulla hem i din nya husvagn eller husbil.",
  },
];

export default function OmOss() {
  return (
    <main className="pt-28 md:pt-36 pb-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <p className="font-mono-brand text-xs md:text-sm tracking-[0.3em] text-electric mb-4">OM OSS</p>
          <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white">
            Ett personligt företag — <span className="text-gradient-blue">med fritiden nära hjärtat</span>
          </h1>
        </Reveal>

        <div className="mt-14 grid gap-12 lg:grid-cols-2 items-center">
          <Reveal>
            <img
              src={ABOUT_IMAGE}
              alt="Husvagn och fritidsliv"
              loading="lazy"
              className="rounded-2xl border border-metallic/70 w-full object-cover aspect-[4/3]"
            />
          </Reveal>
          <div>
            <Reveal delay={0.1}>
              <p className="text-slate-300 leading-relaxed text-lg">
                {SITE.name} drivs av {SITE.owner} — en person med ett stort, äkta intresse för husbilar och
                för att göra riktigt bra affärer. Med personlig service genom hela processen, från första
                samtal till nyckelöverlämning, hjälper vi dig med köp, försäljning och byte av husvagnar
                och husbilar.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-5 text-slate-400 leading-relaxed">
                Vår filosofi är enkel: affärer ska vara smidiga och trygga. Vi tar oss tid att sätta oss in
                i dina behov, ger ärliga värderingar och ser till att du alltid känner dig trygg i
                beslutet — oavsett om det är din första husvagn eller din femte husbil.
              </p>
            </Reveal>
          </div>
        </div>

        {/* TRUST */}
        <div className="mt-24 grid gap-6 md:grid-cols-3" data-testid="om-oss-trust">
          <Reveal className="h-full">
            <div className="h-full rounded-2xl border border-emerald-400/30 bg-emerald-500/5 p-8">
              <FileCheck2 size={26} className="text-emerald-400" />
              <h3 className="mt-5 font-display font-bold text-lg text-white">{SITE.fskatt}</h3>
              <p className="mt-2 text-sm text-slate-400">
                Vi är godkända för F-skattsedel, så du kan känna dig trygg i affären med oss.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="h-full">
            <div className="h-full rounded-2xl border border-metallic/70 bg-surface p-8">
              <BadgeCheck size={26} className="text-electric" />
              <h3 className="mt-5 font-display font-bold text-lg text-white">Organisationsnummer</h3>
              <p className="mt-2 font-mono-brand text-sm text-slate-300">{SITE.orgnr}</p>
            </div>
          </Reveal>
          <Reveal delay={0.2} className="h-full">
            <div className="h-full rounded-2xl border border-metallic/70 bg-surface p-8">
              <Recycle size={26} className="text-brand" />
              <h3 className="mt-5 font-display font-bold text-lg text-white">Vi köper också</h3>
              <p className="mt-2 text-sm text-slate-400">
                Tänker du sälja din husvagn eller husbil? Vi köper fordon direkt — utan krångel.
              </p>
            </div>
          </Reveal>
        </div>

        {/* PROCESS */}
        <div className="mt-24">
          <SectionHeading eyebrow="SÅ FUNGERAR DET" title="Från kontakt till körning" />
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1} className="h-full">
                <div className="relative h-full rounded-2xl border border-metallic/70 bg-surface p-8">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand/15 border border-brand/30 text-electric">
                    <s.icon size={20} />
                  </span>
                  <h3 className="mt-5 font-display font-bold text-lg text-white">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal>
          <div className="mt-20 flex flex-col sm:flex-row items-center justify-between gap-6 rounded-3xl border border-brand/40 bg-surface p-10">
            <p className="font-display font-bold text-xl md:text-2xl text-white text-center sm:text-left">
              Vill du veta mer? Vi finns här.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href={`tel:${SITE.phone}`} data-testid="om-oss-phone-button" className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand-dark transition-colors">
                <Phone size={15} />
                {SITE.phoneDisplay}
              </a>
              <Link to="/kontakt#boka" data-testid="om-oss-booking-link" className="inline-flex items-center gap-2 rounded-full border border-metallic px-6 py-3 text-sm font-semibold text-slate-200 hover:border-electric hover:text-electric transition-colors">
                Boka visning
                <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
